import { useState } from "react";
import SupportOutlinedIcon from "@mui/icons-material/SupportOutlined";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useSupportTicket } from "../../../api/supportTicket";
import CustomModal from "../modal/modal.component";

type SupportTicketCategory = "feature_request" | "bug_report" | "other";

const CATEGORY_OPTIONS: { value: SupportTicketCategory; labelKey: string }[] = [
  { value: "feature_request", labelKey: "Feature request" },
  { value: "bug_report", labelKey: "Bug report" },
  { value: "other", labelKey: "Other" },
];

export const SupportTicketButton = () => {
  const { t } = useTranslation();
  const { loading, error, submit } = useSupportTicket();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<SupportTicketCategory>("bug_report");
  const [message, setMessage] = useState("");
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);

  const resetForm = () => {
    setCategory("bug_report");
    setMessage("");
    setTicketNumber(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    const result = await submit({ category, message });
    if (result?.success) {
      setTicketNumber(result.data.ticket_number);
    }
  };

  const content = ticketNumber ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="success">
        {t("Thank you! Your ticket {{number}} has been sent to our team.", {
          number: ticketNumber,
        })}
      </Alert>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleClose}>
          {t("Close")}
        </Button>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>{t("Type")}</InputLabel>
        <Select
          label={t("Type")}
          value={category}
          onChange={(event: SelectChangeEvent) =>
            setCategory(event.target.value as SupportTicketCategory)
          }
        >
          {CATEGORY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label={t("How can we help?")}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        multiline
        minRows={4}
        fullWidth
      />

      {error ? (
        <Alert severity="error">
          {t("We could not send your ticket. Please try again.")}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button variant="text" onClick={handleClose}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !message.trim()}
        >
          {loading ? t("Sending...") : t("Send")}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        aria-label={t("Help")}
        sx={{ p: 0.5 }}
        data-tour="help"
      >
        <SupportOutlinedIcon />
      </IconButton>

      <CustomModal
        opened={open}
        setOpened={(value) => {
          if (value) {
            setOpen(true);
          } else {
            handleClose();
          }
        }}
        title={t("Contact support")}
        width={480}
        content={content}
      />
    </>
  );
};
